import React, { useState, useEffect } from "react";
import { Check, Search, Filter, Info } from "lucide-react";
import Dropdown from "./Dropdown";
import FilterModal from "./FilterModal";
import { getUtilityPlans, assignPlan } from "../../api"; // Adjust the import path as necessary
import { useToast } from "@shared/hooks/use-toast";

// Keep these if they are truly static or move them to a constants file
const premiseOptions = [
  { value: "residential", label: "Residential Area" },
  { value: "commercial", label: "Commercial Area" },
  { value: "industrial", label: "Industrial Area" },
];

const cityOptions = [
  { value: "new-york", label: "New York" },
  { value: "los-angeles", label: "Los Angeles" },
  { value: "chicago", label: "Chicago" },
];

const PlanMapping = ({
  tableData,
  setTableData,
  selectedRows,
  setSelectedRows,
  searchTerm,
  setSearchTerm,
  // activeTab, // This prop is not directly used in PlanMapping for now, but kept for consistency
  progress,
  getMappedCount,
  tableColumns, // This is the prop we'll use for dynamic rendering
  uploadId,
  setHasPlanIssue,
}) => {
  const { toast } = useToast();
  const [selectedPlanId, setSelectedPlanId] = useState(""); // Stores the 'id' from the API
  const [availablePlanOptions, setAvailablePlanOptions] = useState([]); // Stores fetched plans
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [filters, setFilters] = useState({ city: "", premise: "" });
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [errorFetchingPlans, setErrorFetchingPlans] = useState(null);
  const remoteUtilityId = useRemoteUtilityId();

  useEffect(() => {
    console.log("All props received in PlanMapping:", {
      tableData,
      tableColumns,
    });
    // console.log("tableColumns", tableColumns); // Keep this for debugging if needed
    const fetchPlans = async () => {
      try {
        setLoadingPlans(true);
        setErrorFetchingPlans(null);
        // You might need to pass the remote_utility_id from a parent component or context
        const data = await getUtilityPlans(remoteUtilityId);
        if (data && data.result) {
          const formattedOptions = data.result.map((plan) => ({
            value: plan.id, // Use id as the value
            label: plan.planName, // Use plan_name as the label for display
          }));
          setAvailablePlanOptions(formattedOptions);
        } else {
          setAvailablePlanOptions([]);
        }
      } catch (error) {
        console.error("Failed to fetch utility plans:", error);
        setErrorFetchingPlans("Failed to load plans. Please try again.");
      } finally {
        setLoadingPlans(false);
      }
    };

    fetchPlans();
  }, []); // Empty dependency array means this runs once on mount

  const handleAssignToSelected = async () => {
    if (selectedRows.length === 0 || !selectedPlanId) return;

    const selectedConsumerNos = tableData
      .filter((row) => selectedRows.includes(row.id))
      .map((row) => row.consumerNo);

    const payload = {
      consumer_no: selectedConsumerNos,
      remote_plan_id: selectedPlanId,
      data_upload_id: uploadId,
    };

    try {
      const response = await assignPlan(payload);
      console.log("Plan assigned successfully:", response);

      setTableData((prev) =>
        prev.map((row) =>
          selectedRows.includes(row.id)
            ? {
                ...row,
                planName: availablePlanOptions.find(
                  (opt) => opt.value === selectedPlanId
                )?.label,
                planId: selectedPlanId,
              }
            : row
        )
      );

      setSelectedRows([]);
      setSelectedPlanId("");

      // ✅ Mark issue as resolved in parent
      setHasPlanIssue(false);
      toast({
        title: "Success",
        description: "Plan assignment successfully.",
        variant: "default",
      });
    } catch (error) {
      console.error("Error assigning plan:", error);
    }
  };

  const handleRowSelect = (rowId) => {
    setSelectedRows((prev) =>
      prev.includes(rowId)
        ? prev.filter((id) => id !== rowId)
        : [...prev, rowId]
    );
  };

  const handleSelectAll = () => {
    if (selectedRows.length === tableData.length && tableData.length > 0) {
      setSelectedRows([]);
    } else {
      setSelectedRows(tableData.map((row) => row.id));
    }
  };

  // Filter data based on search term
  const filteredData = tableData.filter((row) =>
    Object.values(row).some(
      (value) =>
        value && String(value).toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  const handleFilterApply = (newFilters) => {
    setFilters(newFilters);
    console.log("Applied filters for Plan Mapping:", newFilters);
  };

  return (
    <>
      {/* Progress Card */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-start space-x-3">
          <Info className="h-5 w-5 text-blue-600 mt-0.5" />
          <div>
            <h3 className="font-medium text-blue-900">Plan Mapping</h3>
            <p className="text-sm text-blue-700 mt-1">
              Assign plans to consumers based on your criteria
            </p>
            <p className="text-sm text-blue-600 mt-1">
              Progress: {progress}% ({getMappedCount("plan")}/{tableData.length}{" "}
              records mapped)
            </p>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        {/* Search and Assignment Controls */}
        <div className="flex items-center justify-between mb-6">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search size={16} className="text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search records..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-64"
            />
          </div>

          <div className="flex items-center space-x-4">
            {loadingPlans ? (
              <div className="text-gray-500">Loading plans...</div>
            ) : errorFetchingPlans ? (
              <div className="text-red-500">{errorFetchingPlans}</div>
            ) : (
              <Dropdown
                options={availablePlanOptions}
                value={selectedPlanId}
                onChange={setSelectedPlanId} // Update selectedPlanId with the ID
                placeholder="Select plan"
                className="w-40"
              />
            )}

            <button
              onClick={handleAssignToSelected}
              disabled={selectedRows.length === 0 || !selectedPlanId}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                selectedRows.length > 0 && selectedPlanId
                  ? "bg-blue-600 text-white hover:bg-blue-700"
                  : "bg-gray-300 text-gray-500 cursor-not-allowed"
              }`}
            >
              <Check size={16} />
              <span>Assign to Selected</span>
            </button>

            <button
              onClick={() => setIsFilterModalOpen(true)}
              className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <Filter size={16} />
              <span>Filter</span>
            </button>
          </div>
        </div>

        {/* Table - NOW DYNAMICALLY RENDERED */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="w-8 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={
                      selectedRows.length === tableData.length &&
                      tableData.length > 0
                    }
                    onChange={handleSelectAll}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </th>
                {/* Dynamically render headers from tableColumns */}
                {tableColumns.map((column) => (
                  <th
                    key={column.accessor}
                    className="text-left py-3 px-4 font-medium text-gray-700 text-sm"
                  >
                    {column.Header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredData.map((row) => (
                <tr key={row.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    {/* <input
                      type="checkbox"
                      checked={selectedRows.includes(row.id)}
                      onChange={() => handleRowSelect(row.id)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    /> */}
                    <input
                      type="checkbox"
                      checked={selectedRows.includes(row.id)} // reflect "Select All" state
                      disabled // prevent manual change
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 opacity-50 cursor-not-allowed"
                    />
                  </td>
                  {/* Dynamically render cells based on tableColumns */}
                  {tableColumns.map((column) => (
                    <td
                      key={`${row.id}-${column.accessor}`}
                      className="px-4 py-3 text-sm"
                    >
                      {/* Special handling for planName and serviceArea */}
                      {column.accessor === "planName" ? (
                        row.planName ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {row.planName}
                          </span>
                        ) : (
                          <span className="text-gray-400">Not assigned</span>
                        )
                      ) : column.accessor === "serviceArea" ? (
                        // Render Service Area for completeness, even if not directly mapped here
                        row.serviceArea ? (
                          <span className="inline-flex items-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            {row.serviceArea}
                          </span>
                        ) : (
                          <span className="text-gray-400">Not assigned</span>
                        )
                      ) : (
                        // Generic rendering for all other dynamic columns
                        <span className="text-gray-900">
                          {/* Ensure value is not null/undefined and convert to string */}
                          {row[column.accessor] !== null &&
                          row[column.accessor] !== undefined
                            ? String(row[column.accessor])
                            : ""}
                        </span>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Filter Modal */}
      <FilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        onApply={handleFilterApply}
        premiseOptions={premiseOptions}
        cityOptions={cityOptions}
      />
    </>
  );
};

export default PlanMapping;
